import { moneyAmountLabel } from '@/utils/formatCurrency';
import { cn } from '@/utils/cn';
import {
  SP_AUTO_TARGETING_GROUPS,
  seedAutoTargetingGroupBids,
  type SpAutoTargetingGroupState,
  type SpAutoTargetingMode,
} from '@/utils/spAdGroupCreate';

export function AutoTargetingFields({
  defaultBid,
  onDefaultBidChange,
  targetingMode,
  onTargetingModeChange,
  groups,
  onGroupsChange,
  disabled,
  framed = true,
}: {
  defaultBid: string;
  onDefaultBidChange: (value: string) => void;
  targetingMode: SpAutoTargetingMode;
  onTargetingModeChange: (mode: SpAutoTargetingMode) => void;
  groups: SpAutoTargetingGroupState[];
  onGroupsChange: (groups: SpAutoTargetingGroupState[]) => void;
  disabled?: boolean;
  framed?: boolean;
}) {
  const body = (
    <div className="space-y-3">
      <p className="text-xs text-foreground-muted">
        Amazon will match shopper searches and products to your advertised ASINs. Choose a default bid
        for every group, or set custom bids.
      </p>
      <div>
        <label className="block text-sm font-medium text-foreground-muted mb-1">
          {moneyAmountLabel('Default bid')}
        </label>
        <input
          type="number"
          step="0.01"
          min="0.01"
          value={defaultBid}
          onChange={(e) => {
            const next = e.target.value;
            onGroupsChange(seedAutoTargetingGroupBids(groups, defaultBid, next));
            onDefaultBidChange(next);
          }}
          className="w-40 border border-border rounded-lg px-3 py-2 text-sm"
          disabled={disabled}
          aria-label="Default bid"
        />
      </div>
      <label className="flex items-start gap-2 text-sm cursor-pointer">
        <input
          type="radio"
          name="auto-targeting-mode"
          checked={targetingMode === 'default'}
          onChange={() => onTargetingModeChange('default')}
          disabled={disabled}
          className="mt-0.5"
        />
        <span>
          <span className="font-medium text-foreground">Use default bid</span>
          <span className="block text-xs text-foreground-muted">
            Apply the default bid to close match, loose match, substitutes, and complements.
          </span>
        </span>
      </label>
      <label className="flex items-start gap-2 text-sm cursor-pointer">
        <input
          type="radio"
          name="auto-targeting-mode"
          checked={targetingMode === 'custom'}
          onChange={() => onTargetingModeChange('custom')}
          disabled={disabled}
          className="mt-0.5"
        />
        <span>
          <span className="font-medium text-foreground">Set custom bids</span>
          <span className="block text-xs text-foreground-muted">
            Turn groups on or off and set a bid for each targeting group.
          </span>
        </span>
      </label>
      {targetingMode === 'custom' ? (
        <div className="border border-border rounded-xl overflow-hidden">
          <div className="grid grid-cols-[1fr_auto] gap-3 px-3 py-2 bg-page text-xs font-medium text-foreground-muted uppercase tracking-wide">
            <span>Targeting groups</span>
            <span>{moneyAmountLabel('Bid')}</span>
          </div>
          <ul className="divide-y divide-border">
            {SP_AUTO_TARGETING_GROUPS.map((def) => {
              const row = groups.find((group) => group.id === def.id);
              if (!row) return null;
              return (
                <li key={def.id} className="grid grid-cols-[1fr_auto] gap-3 items-center px-3 py-3">
                  <div className="flex items-center gap-3 min-w-0">
                    <button
                      type="button"
                      role="switch"
                      aria-checked={row.enabled}
                      aria-label={`${def.label} targeting`}
                      disabled={disabled}
                      onClick={() =>
                        onGroupsChange(
                          groups.map((group) =>
                            group.id === def.id ? { ...group, enabled: !group.enabled } : group
                          )
                        )
                      }
                      className={cn(
                        'relative h-6 w-10 shrink-0 rounded-full transition-colors',
                        row.enabled ? 'bg-brand' : 'bg-surface-muted'
                      )}
                    >
                      <span
                        className={cn(
                          'absolute top-0.5 h-5 w-5 rounded-full bg-white shadow-sm transition-[left,right]',
                          row.enabled ? 'right-0.5' : 'left-0.5'
                        )}
                      />
                    </button>
                    <span className="text-sm font-medium text-foreground">{def.label}</span>
                  </div>
                  <input
                    type="number"
                    step="0.01"
                    min="0.01"
                    value={row.bid}
                    onChange={(e) =>
                      onGroupsChange(
                        groups.map((group) =>
                          group.id === def.id ? { ...group, bid: e.target.value } : group
                        )
                      )
                    }
                    className="w-24 border border-border rounded-lg px-2 py-1.5 text-sm text-right"
                    disabled={disabled || !row.enabled}
                    aria-label={`${def.label} bid`}
                  />
                </li>
              );
            })}
          </ul>
        </div>
      ) : null}
    </div>
  );
  if (!framed) return body;
  return (
    <fieldset className="border border-border rounded-xl p-4 space-y-3">
      <legend className="text-sm font-medium text-foreground-muted px-1">Automatic targeting</legend>
      {body}
    </fieldset>
  );
}
